import React from 'react';
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useSelector } from "react-redux";

export const TableGridSkeleton = ({ columns = [], rows = 6 }) => {
  const themeMode = useSelector(state => state.themeMode?.themeMode || 'light')

  //  Improved column layout mapping with better width distribution
  const getGridTemplate = () =>
    columns
      .map((col) => {
        const key = (col.accessor || col.Header || "").toLowerCase();

        // More precise column widths
        if (key === "no" || key === "rowno") return "60px";
        if (key.includes("action")) return "100px";
        if (key.includes("image")) return "120px";
        if (key.includes("status")) return "120px";
        if (key.includes("email")) return "minmax(200px, 1.5fr)";
        if (key.includes("about")) return "minmax(250px, 2fr)";

        return "minmax(120px, 1fr)";
      })
      .join(" ");

  // Enhanced cell rendering with better visual hierarchy
  const renderCell = (col) => {
    const key = (col.accessor || col.Header || "").toLowerCase();

    // Row number
    if (key === "no" || key === "rowno") {
      return (
        <div style={{ paddingLeft: '12px' }}>
          <Skeleton width={24} height={16} />
        </div>
      );
    }

    // Image with avatar style
    if (key.includes("image")) {
      return (
        <div className="d-flex align-items-center gap-3">
          <Skeleton
            circle
            width={48}
            height={48}
            style={{ flexShrink: 0 }}
          />
        </div>
      );
    }

    // Status badge
    if (key.includes("status")) {
      return (
        <Skeleton
          width={80}
          height={28}
          borderRadius={20}
          style={{ display: 'inline-block' }}
        />
      );
    }

    // Action buttons
    if (key.includes("action")) {
      return (
        <div className="d-flex gap-2 justify-content-end" style={{ paddingRight: '12px' }}>
          <Skeleton width={32} height={32} borderRadius={6} />
          <Skeleton width={32} height={32} borderRadius={6} />
        </div>
      );
    }

    // Email - longer skeleton
    if (key.includes("email")) {
      return <Skeleton width="85%" height={16} />;
    }

    // About - multi-line appearance
    if (key.includes("about")) {
      return (
        <div className="d-flex flex-column gap-1">
          <Skeleton width="90%" height={14} />
          <Skeleton width="60%" height={14} />
        </div>
      );
    }

    // Default text field
    return <Skeleton width="75%" height={16} />;
  };

  return (
    <SkeletonTheme
      baseColor={themeMode === 'dark' ? "#2b2b2b" : "#e6e8eb"}
      highlightColor={themeMode === 'dark' ? "#3a3a3a" : "#f2f4f7"}
    >
      <div className="w-100">
        {/* ===== Header Row ===== */}
        <div
          className="d-grid align-items-center mb-2 pb-3 border-bottom"
          style={{
            gridTemplateColumns: getGridTemplate(),
            gap: '16px'
          }}
        >
          {columns.map((col, i) => {
            const key = (col.accessor || col.Header || "").toLowerCase();
            const isAction = key.includes("action");

            return (
              <div
                key={i}
                style={{
                  textAlign: isAction ? 'right' : 'left',
                  paddingLeft: i === 0 ? '12px' : '0',
                  paddingRight: isAction ? '12px' : '0'
                }}
              >
                <Skeleton width={isAction ? "50%" : "70%"} height={14} />
              </div>
            );
          })}
        </div>

        {/* ===== Data Rows ===== */}
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div
            key={rowIndex}
            className="d-grid align-items-center border-bottom"
            style={{
              gridTemplateColumns: getGridTemplate(),
              gap: '16px',
              padding: '16px 0',
              minHeight: '72px'
            }}
          >
            {columns.map((col, colIndex) => (
              <div key={colIndex}>{renderCell(col)}</div>
            ))}
          </div>
        ))}
      </div>
    </SkeletonTheme>
  );
};

// Demo component to show usage
const DemoTable = () => {
  const columns = [
    { Header: "No", accessor: "rowNo" },
    { Header: "Image", accessor: "Image" },
    { Header: "Email", accessor: "Email" },
    { Header: "First Name", accessor: "First Name" },
    { Header: "Last Name", accessor: "Last Name" },
    { Header: "About", accessor: "About" },
    { Header: "Status", accessor: "Status" },
    { Header: "Action", accessor: "action", sortable: false }
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{
        background: 'white',
        borderRadius: '8px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ margin: 0, marginBottom: '8px' }}>User List</h2>
          <Skeleton width={200} height={12} />
        </div>

        <TableGridSkeleton columns={columns} rows={5} />

        <div style={{
          marginTop: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Skeleton width={150} height={14} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <Skeleton width={32} height={32} borderRadius={4} />
            <Skeleton width={32} height={32} borderRadius={4} />
            <Skeleton width={32} height={32} borderRadius={4} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoTable;